import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/shared/button';
import { cn } from '../../lib/utils';
import FileUpload from './FileUpload';
import { AuthError } from '../../hooks/useAuth';

interface PodcastFormProps {
  onSubmit: (text: string, fileContent: string, fileType: string, deepResearch: boolean) => Promise<void>;
}

const MAX_WORDS = 500;

const SUPPORTED_FILE_TYPES = [
  'text/plain',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '', // allow empty for text-only
];

/**
 * PodcastForm Component
 *
 * A form component that handles text input and file uploads for podcast generation.
 * Features:
 * - Text input with character limit
 * - File upload support
 * - Word count and duration estimation
 * - Keyboard shortcuts
 * - Draft saving
 * - Deep research mode toggle
 */
const PodcastForm: React.FC<PodcastFormProps> = ({ onSubmit }) => {
  // Form state
  const [text, setText] = useState('');
  const [fileContent, setFileContent] = useState<string>('');
  const [fileType, setFileType] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [deepResearch, setDeepResearch] = useState(false);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!(text.trim() || fileContent.trim())) {
      console.log('Form submission blocked: No content provided');
      return;
    }

    if (!SUPPORTED_FILE_TYPES.includes(fileType)) {
      console.log('Form submission blocked: Unsupported file type');
      return;
    }

    setIsLoading(true);

    try {
      await onSubmit(text, fileContent, fileType, deepResearch);
    } catch (error) {
      if (error instanceof AuthError) {
        // Don't show error for auth issues since we're showing the login modal
        return;
      }
    } finally {
      setIsLoading(false);
    }
  }, [text, fileContent, fileType, deepResearch, onSubmit]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Generate shortcut
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        if (text.trim() || fileContent.trim()) {
          console.log('Generate shortcut triggered');
          handleSubmit(e as any);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [text, fileContent, handleSubmit]);

  // Handle text input changes
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    const words = newText.trim().split(/\s+/).filter(Boolean).length;

    if (words <= MAX_WORDS) {
      setText(newText);
      setWordCount(words);
    }
  };

  // Get color for character count display
  const getWordCountColor = () => {
    if (wordCount > MAX_WORDS * 0.9) return 'text-red-500';
    if (wordCount > MAX_WORDS * 0.8) return 'text-yellow-500';
    return 'text-gray-400';
  };

  // Calculate word count
  const getWordCount = (text: string) => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  // Load saved draft
  useEffect(() => {
    const savedDraft = localStorage.getItem('podcast-draft');
    if (savedDraft) {
      console.log('Loading saved draft');
      setText(savedDraft);
      setWordCount(savedDraft.length);
    }
  }, []);

  // Save draft with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (text.trim()) {
        console.log('Saving draft');
        localStorage.setItem('podcast-draft', text);
      } else {
        localStorage.removeItem('podcast-draft');
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [text]);

  // Handle loading messages
  useEffect(() => {
    if (isLoading) {
      const messages = deepResearch
        ? [
            'Processing text with deep research...',
            'Analyzing content in detail...',
            'Generating enhanced audio...',
            'Almost there...',
          ]
        : [
            'Processing text...',
            'Generating audio...',
            'Almost there...',
          ];
      let i = 0;
      const interval = setInterval(() => {
        setLoadingMessage(messages[i % messages.length]);
        i++;
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isLoading, deepResearch]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = document.querySelector('textarea');
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 400) + 'px';
    }
  }, [text]);

  // Handle file upload
  const handleFileUpload = (content: string, type: string) => {
    console.log('File uploaded:', {
      contentLength: content.length,
      wordCount: getWordCount(content),
      fileType: type
    });
    setFileContent(content);
    setFileType(type);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-4">
        {/* Text Input Section */}
        <div className="flex justify-between items-center">
          <label className="text-sm font-medium text-gray-700">
            Enter Text to Generate
          </label>
          <div className="flex items-center space-x-2">
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setDeepResearch(!deepResearch)}
                className={cn(
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                  deepResearch ? "bg-blue-600" : "bg-gray-200"
                )}
                role="switch"
                aria-checked={deepResearch}
                aria-labelledby="deep-research-label"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                    deepResearch ? "translate-x-5" : "translate-x-0"
                  )}
                />
              </button>
              <span id="deep-research-label" className="ml-2 text-sm text-gray-600">
                Deep Research Mode
              </span>
            </div>
            <kbd className="text-xs px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-md text-gray-500">
              {/Mac|iPhone|iPod|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl'} + Enter
            </kbd>
          </div>
        </div>

        {/* Textarea with Word Count */}
        <div className="relative">
          <textarea
            value={text}
            onChange={handleTextChange}
            className={cn(
              "w-full min-h-[8rem] max-h-[400px] p-4 text-base rounded-lg border focus:outline-none transition-all",
              "border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500",
              "placeholder:text-gray-400",
              "resize-none",
              wordCount > MAX_WORDS * 0.9 && "border-red-300 focus:border-red-500 focus:ring-red-500/20"
            )}
            placeholder="Type or paste your text here..."
            disabled={isLoading}
          />
          <div className={cn(
            "absolute bottom-3 right-3 text-xs px-2 py-0.5 rounded-md bg-gray-100",
            getWordCountColor()
          )}>
            {wordCount} / {MAX_WORDS} words
          </div>
        </div>

        {/* File Upload */}
        <FileUpload onFileSelect={handleFileUpload} />
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isLoading}
        className={cn(
          "w-full bg-gradient-to-r from-blue-600 to-violet-600 text-white",
          "shadow-sm hover:shadow transition-all duration-200",
          "rounded-lg py-2.5",
          "text-sm font-medium relative overflow-hidden",
          "disabled:opacity-80 disabled:cursor-wait"
        )}
      >
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>{loadingMessage}</span>
          </div>
        ) : (
          'Generate Podcast'
        )}
        {isLoading && (
          <div
            className="absolute bottom-0 left-0 h-1 bg-white/20 w-full"
          >
            <div
              className="h-full bg-white/40 animate-progress-indeterminate"
              style={{
                width: '30%',
              }}
            />
          </div>
        )}
      </Button>
    </form>
  );
};

export default PodcastForm;
